import { BCMSRecord, BCMSRecordProperty } from "@bcm-dev/bcms-vite";
import { Account, Payment } from "model";
/**
* @class PaymentInquiry
* @classdesc Class definition for record type from model
* @param {Class} record - record from model to init class
* @extends BCMSRecord
* @example const record = model.paymentInquiries[0];
* const paymentInquiry = new PaymentInquiry(record);
* @property {Number} _id - The id of the payment inquiry
* @property {String} _confirmationNumber - The confirmation number of the payment inquiry
* @property {Payment} _payment - The payment of the payment inquiry
* @property {Account} _payFromAccount - The pay from account of the payment inquiry
* @property {String} _reason - The reason of the payment inquiry
* @property {Boolean} _lateFeesAccessed - Whether late fees were assessed
* @property {Currency} _lateFeesAmount - Whether late fees have an amount
* @property {Boolean} _billerWaivedLateFeeCharge - Whether the biller waived the late fee charge
* @property {Boolean} _financeChargesAssessed - Whether finance charges were assessed
* @property {Currency} _financeChargesAmount - The amount of the finance charges
* @property {Boolean} _billerWaivedFinanceCharges - Whether biller waived the finance charges
* @property {String} _contactName - Contact name for the payment inquiry
* @property {String} _phoneNumber - Phone number for the payment inquiry
* @property {BCMSDate} _contactDate - Contact date for the payment inquiry
* @property {String} _additionalComments - Additional comments for the payment inquiry
*/
export default class PaymentInquiry extends BCMSRecord{
    _id;
    _confirmationNumber;
    _payment;
    _payFromAccount;
    _reason;
    _lateFeesAccessed;
    _lateFeesAmount;
    _billerWaivedLateFeeCharge;
    _financeChargesAssessed;
    _financeChargesAmount;
    _billerWaivedFinanceCharges;
    _contactName;
    _phoneNumber;
    _contactDate;
    _additionalComments;

    constructor (record){
      super(record);      
    }
  }

PaymentInquiry.define({
    "recordType":"paymentInquiries",
    "recordIDProp":"paymentInquiryID",
    "className" : "PaymentInquiry",
    "recordProperties":[
    /** 
     * @name id
     * @instance
     * @property get {Number} - gets id of the payment inquiry
     * @property set {Number} - sets id of the payment inquiry
     * @memberof PaymentInquiry
     */
        BCMSRecordProperty.mapToNumber("id","_id","paymentInquiryID"),
    /** 
     * @name confirmationNumber
     * @instance
     * @property get {String} - gets confirmation number of the payment inquiry
     * @property set {String} - sets confirmation number of the payment inquiry
     * @memberof PaymentInquiry
     */
        BCMSRecordProperty.mapToString("confirmationNumber","_confirmationNumber", "paymentInquiryConfirmationNumber"),
    /** 
     * @name payment
     * @instance
     * @property get {Payment} - gets the payment of the payment inquiry
     * @property set {Payment} - sets the payment of the payment inquiry
     * @memberof PaymentInquiry
     */
        BCMSRecordProperty.mapToClass("payment","_payment", () => Payment, "paymentInquiryPayment"),
    /** 
     * @name payFromAccount
     * @instance
     * @property get {Account} - gets the payment of the payment inquiry
     * @property set {Account} - sets the payment of the payment inquiry
     * @memberof PaymentInquiry
     */
        BCMSRecordProperty.mapToClass("payFromAccount","_payFromAccount", () => Account, "paymentInquiryPayFrom"),
    /** 
     * @name reason
     * @instance
     * @property get {String} - gets the reason of the payment inquiry
     * @property set {String} - sets the reason of the payment inquiry
     * @memberof PaymentInquiry
     */
        BCMSRecordProperty.mapToString("reason","_reason", "paymentInquiryReason"),
    /** 
     * @name lateFeesAccessed
     * @instance
     * @property get {Boolean} - gets the lateFeesAccessed value of the payment inquiry
     * @property set {Boolean} - sets the lateFeesAccessed value of the payment inquiry
     * @memberof PaymentInquiry
     */
        BCMSRecordProperty.mapToBoolean("lateFeesAccessed","_lateFeesAccessed", "paymentInquiryLateFeesAccessed"),
    /** 
     * @name lateFeesAmount
     * @instance
     * @property get {BCMSCurrency} - gets the lateFeesAmount value of the payment inquiry
     * @property set {BCMSCurrency} - sets the lateFeesAmount value of the payment inquiry
     * @memberof PaymentInquiry
     */
        BCMSRecordProperty.mapToCurrency("lateFeesAmount","_lateFeesAmount", "paymentInquiryLateFeesAmount"),
    /** 
     * @name billerWaivedLateFeeCharge
     * @instance
     * @property get {Account} - gets the billerWaivedLateFeeCharge value of the payment inquiry
     * @property set {Account} - sets the billerWaivedLateFeeCharge value of the payment inquiry
     * @memberof PaymentInquiry
     */
        BCMSRecordProperty.mapToBoolean("billerWaivedLateFeeCharge","_billerWaivedLateFeeCharge", "paymentInquiryBillerWaivedLateFeeCharge"),
    /** 
     * @name financeChargesAssessed
     * @instance
     * @property get {Account} - gets the financeChargesAssessed value of the payment inquiry
     * @property set {Account} - sets the financeChargesAssessed value of the payment inquiry
     * @memberof PaymentInquiry
     */
        BCMSRecordProperty.mapToBoolean("financeChargesAssessed","_financeChargesAssessed", "paymentInquiryFinanceChargesAssessed"),
    /** 
     * @name financeChargesAmount
     * @instance
     * @property get {Account} - gets the financeChargesAmount value of the payment inquiry
     * @property set {Account} - sets the financeChargesAmount value of the payment inquiry
     * @memberof PaymentInquiry
     */
        BCMSRecordProperty.mapToCurrency("financeChargesAmount","_financeChargesAmount", "paymentInquiryFinanceChargesAmount"),
    /** 
     * @name billerWaivedFinanceCharges
     * @instance
     * @property get {Account} - gets the billerWaivedFinanceCharges value of the payment inquiry
     * @property set {Account} - sets the billerWaivedFinanceCharges value of the payment inquiry
     * @memberof PaymentInquiry
     */
        BCMSRecordProperty.mapToBoolean("billerWaivedFinanceCharges","_billerWaivedFinanceCharges", "paymentInquiryBillerWaivedFinanceCharges"),
    /** 
     * @name contactName
     * @instance
     * @property get {Account} - gets the contactName value of the payment inquiry
     * @property set {Account} - sets the contactName value of the payment inquiry
     * @memberof PaymentInquiry
     */
        BCMSRecordProperty.mapToString("contactName","_contactName", "paymentInquiryContactName"),
    /** 
     * @name phoneNumber
     * @instance
     * @property get {Account} - gets the phoneNumber value of the payment inquiry
     * @property set {Account} - sets the phoneNumber value of the payment inquiry
     * @memberof PaymentInquiry
     */
        BCMSRecordProperty.mapToString("phoneNumber","_phoneNumber", "paymentInquiryPhoneNumber"),
    /** 
     * @name contactDate
     * @instance
     * @property get {Account} - gets the contactDate value of the payment inquiry
     * @property set {Account} - sets the contactDate value of the payment inquiry
     * @memberof PaymentInquiry
     */
        BCMSRecordProperty.mapToDate("contactDate","_contactDate", "paymentInquiryContactDate"),
    /** 
     * @name additionalComments
     * @instance
     * @property get {Account} - gets the additionalComments value of the payment inquiry
     * @property set {Account} - sets the additionalComments value of the payment inquiry
     * @memberof PaymentInquiry
     */
        BCMSRecordProperty.mapToString("additionalComments","_additionalComments", "paymentInquiryAdditionalComments"),
    ]
})




